import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { Video, VideoResourceMapping } from "@/lib/cms";
import { mappedDispatchSlug } from "@/lib/cms";

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

function formatPublishedMonth(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return DATE_FMT.format(d);
}

function buildEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    autoplay: "1",
    rel: "0",
    modestbranding: "1",
  });
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params}`;
}

export type VideoPlayerModalProps = {
  video: Video;
  mapping?: VideoResourceMapping | null;
  onClose: () => void;
};

export default function VideoPlayerModal({
  video,
  mapping,
  onClose,
}: VideoPlayerModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const dispatchSlug = mapping ? mappedDispatchSlug(mapping) : null;
  const ctaText = mapping?.ctaText ?? "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 font-fira"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/80 backdrop-blur-sm"
      />

      <div
        className="relative z-10 flex w-full max-w-4xl flex-col gap-5 rounded-2xl border border-white/10 bg-[#0F0F0F] p-4 shadow-[0_20px_60px_rgba(0,0,0,0.55)] md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-black/60 text-white/80 transition hover:bg-white/10 hover:text-white"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.25}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black">
          <iframe
            src={buildEmbedUrl(video.youtubeVideoId)}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 h-full w-full"
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[12px] text-white/55 md:text-[13px]">
            {formatPublishedMonth(video.publishedAt)
              ? `${formatPublishedMonth(video.publishedAt)} · `
              : ""}
            {video.viewCountFormatted ?? ""}
          </p>
          <h2 className="font-fira-condensed text-[20px] font-semibold leading-tight tracking-tight text-white md:text-[24px]">
            {video.title}
          </h2>
          {video.description ? (
            <p className="text-sm leading-relaxed text-[#CFCFCFE5] md:text-[15px]">
              {video.description}
            </p>
          ) : null}
        </div>

        {mapping && dispatchSlug ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-[0.12em] text-white/45">
              Resources with the video
            </p>
            <Link
              to={`/daily-ai-updates/${dispatchSlug}`}
              onClick={onClose}
              className="inline-flex w-full items-center justify-center rounded-lg border border-[#858585] bg-black px-5 py-2.5 font-fira text-sm font-semibold text-[#FFFFFF] transition-opacity hover:opacity-90 md:text-[0.9375rem]"
            >
              {ctaText}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}